import type { FieldValues } from "react-hook-form";
import { useI18nContext } from "@hopper-b2b/i18n";
import { specialCharacterRegex } from "@hopper-b2b/utilities";

export enum DriverLicenseFormFields {
  address = "licenseAddress",
  issuingCity = "licenseIssuingCity",
  issuingCountry = "licenseIssuingCountry",
  postalCode = "licensePostalCode",
}

export type DriverLicenseFormContext = {
  [DriverLicenseFormFields.address]?: string;
  [DriverLicenseFormFields.issuingCity]?: string;
  [DriverLicenseFormFields.issuingCountry]: string;
  [DriverLicenseFormFields.postalCode]: string;
};

export type DriverLicenseFormProps = {
  showAddress?: boolean;
  showCity?: boolean;
  // When true (default), issuing country is pre-filled with the brand's
  // preferred country code. Set false to leave it empty so the user must
  // make an explicit choice (mirrors `prefillNationality` from HSBC-1316).
  prefillIssuingCountry?: boolean;
};
export const useDriverLicenseFormFields = ({
  showAddress = false,
  showCity = false,
  prefillIssuingCountry = true,
}: DriverLicenseFormProps) => {
  const { t, brand } = useI18nContext();

  const driverLicenseDetailsDefaultValues: DriverLicenseFormContext = {
    [DriverLicenseFormFields.address]: "",
    [DriverLicenseFormFields.issuingCity]: "",
    [DriverLicenseFormFields.issuingCountry]: prefillIssuingCountry
      ? brand.preferredCountryCode
      : "",
    [DriverLicenseFormFields.postalCode]: "",
  };

  const formFields: Partial<Record<DriverLicenseFormFields, FieldValues>> = {
    [DriverLicenseFormFields.issuingCountry]: {
      required: t("travelerForm.errors.driverLicenseissueCountryRequired"),
    },
    [DriverLicenseFormFields.postalCode]: {
      required: t("travelerForm.errors.driverLicensezipCodeRequired"),
      validate: (issuingCity: string) => {
        if (/\s/.test(issuingCity)) {
          return t("noWhitespaceError");
        }
        if (!specialCharacterRegex.test(issuingCity)) {
          return t("noSpecialCharactersError");
        }
      },
    },
  };

  if (showAddress) {
    formFields[DriverLicenseFormFields.address] = {
      required: t("travelerForm.errors.driverLicenseaddressRequired"),
    };
  }

  if (showCity) {
    formFields[DriverLicenseFormFields.issuingCity] = {
      required: t("travelerForm.errors.driverLicenseissueCityRequired"),
    };
  }

  return { formFields, driverLicenseDetailsDefaultValues };
};
